import DocumentIcon from '@heroicons/react/24/outline/DocumentIcon';
import PhotoIcon from '@heroicons/react/24/outline/PhotoIcon';
import { ComponentPropsWithoutRef, ComponentType } from 'react';

import { useAttributeLabelTranslator } from '@/components/datamodel/hooks/useAttributeLabelTranslator';
import { useFormatAttributeValue } from '@/components/datamodel/utils/formatAttributeValue';
import { useTranslation } from '@/hooks/useTranslation';
import { AssetApiRecord } from '@/pages/dam/api/assets.types';
import { DataModelLayout } from '@/types/DataModel';

import { type DamCardDate, resolveDamCardDate } from './damCardDate';
import { type DamCardSectionAttribute, type DamCardSections, resolveDamCardSections } from './damCardSections';
import { fileFormatOf, isBrowserRenderableImage, isImageMimeType } from './damFileFormat';
import { type DamStatusBadge, useDamStatusBadges } from './damStatusBadges';

const MIME_TYPE = 'mimeType__';
const FILENAME = 'filename__';
const LINKED_ITEM_COUNT = 'linkedItemCount__';
const COMPLIANT = 'compliant__';
const CREATED_AT = 'createdAt__';
const UPDATED_AT = 'updatedAt__';

interface DamAssetField {
    name: string;
    /** Translated attribute label without colon. */
    label: string;
    value: string;
}

interface DamAssetContent {
    sections: DamCardSections;
    isImage: boolean;
    /** `publicUrl__` when the browser can render the format, otherwise `undefined`. */
    imageUrl: string | undefined;
    topBadge: { label: string; icon?: ComponentType<ComponentPropsWithoutRef<'svg'>> } | undefined;
    statusBadges: DamStatusBadge[];
    title: string;
    rows: DamAssetField[];
    footerDate: DamCardDate | undefined;
    footerOthers: DamAssetField[];
}

const hasValue = (value: unknown): boolean =>
    value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0);

/**
 * Display values of one asset per `asset_card` area (D-032), shared by card and list
 * (D-036): file format badge from `mimeType__`, linked products as a count (D-029),
 * the edited/uploaded date rule (D-033), every other attribute formatted generically.
 */
function useDamAssetContent(asset: AssetApiRecord, layout: DataModelLayout | undefined): DamAssetContent {
    const { t } = useTranslation();
    const { formatValue } = useFormatAttributeValue();
    const fieldLabel = useDamFieldLabel();
    const statusBadgesOf = useDamStatusBadges();

    const sections = resolveDamCardSections(layout);
    const mimeType = asset[MIME_TYPE];
    const isImage = isImageMimeType(mimeType);
    const publicUrl = typeof asset.publicUrl__ === 'string' ? asset.publicUrl__ : undefined;

    const formatSingle = (attribute: DamCardSectionAttribute, value: unknown): string =>
        attribute.name === FILENAME ? String(value) : formatValue(attribute, value);

    const toField = (attribute: DamCardSectionAttribute): DamAssetField => {
        if (attribute.name === LINKED_ITEM_COUNT) {
            const count = Number(asset[LINKED_ITEM_COUNT] ?? 0);
            return { name: attribute.name, label: fieldLabel(attribute), value: count > 0 ? String(count) : t('DAM.CARD.LINKED_PRODUCTS_NONE') };
        }
        return { name: attribute.name, label: fieldLabel(attribute), value: formatSingle(attribute, asset[attribute.name]) };
    };

    const topAttribute = sections.TOP_BADGES.find((attribute) => hasValue(asset[attribute.name]));
    const topBadge = topAttribute && (topAttribute.name === MIME_TYPE
        ? { label: fileFormatOf(asset[FILENAME], mimeType), icon: isImage ? PhotoIcon : DocumentIcon }
        : { label: formatSingle(topAttribute, asset[topAttribute.name]) });

    const statusBadges = statusBadgesOf(asset, sections.BOTTOM_BADGES);

    const titleAttribute = sections.TITLE.find((attribute) => hasValue(asset[attribute.name]));

    const footerDateAttributes = sections.FOOTER.filter((attribute) => attribute.name === CREATED_AT || attribute.name === UPDATED_AT);
    const footerHas = (name: string) => footerDateAttributes.some((attribute) => attribute.name === name);

    return {
        sections,
        isImage,
        imageUrl: publicUrl && isBrowserRenderableImage(mimeType) ? publicUrl : undefined,
        topBadge,
        statusBadges,
        title: titleAttribute ? formatSingle(titleAttribute, asset[titleAttribute.name]) : '',
        rows: sections.BODY.map(toField),
        footerDate: footerDateAttributes.length > 0
            ? resolveDamCardDate(footerHas(CREATED_AT) ? asset[CREATED_AT] : undefined, footerHas(UPDATED_AT) ? asset[UPDATED_AT] : undefined)
            : undefined,
        footerOthers: sections.FOOTER
            .filter((attribute) => !footerDateAttributes.includes(attribute) && hasValue(asset[attribute.name]))
            .map(toField),
    };
}

/** DAM texts for system attributes whose data-model label is shared with items (e.g. item exports). */
const DAM_FIELD_LABELS: Record<string, string> = {
    [LINKED_ITEM_COUNT]: 'DAM.FIELD.LINKED_PRODUCTS',
    [COMPLIANT]: 'DAM.FIELD.COMPLIANCE',
};

/** Label of a `BODY` field without colon, e.g. for list column headers. */
function useDamFieldLabel() {
    const { t } = useTranslation();
    const { translateAttributeLabel } = useAttributeLabelTranslator();
    return (attribute: DamCardSectionAttribute): string =>
        DAM_FIELD_LABELS[attribute.name] ? t(DAM_FIELD_LABELS[attribute.name]) : translateAttributeLabel(attribute);
}

export { useDamAssetContent, useDamFieldLabel };
export type { DamAssetContent, DamAssetField };
